import {useDroppable} from '@dnd-kit/core';
import {isEqual} from 'lodash';
import React, {useCallback, useMemo, useRef} from 'react';
import {twMerge} from 'tailwind-merge';

import {useSelector} from '../../../state/hooks';
import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {useShowPinnedBorder} from '../../SettingControls/useIsSectionPinned';
import {SectionSettingsContextProvider} from '../../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {usePanelBankSectionIsOpen} from '../hooks/panelBankSectionIsOpenHooks';
import {type SectionWithRef} from '../types';
import {PanelBankSectionHeader} from './sectionHeader/PanelBankSectionHeader';
import {getIsHiddenPanelsSection} from './useIsSectionVisible';
import {useSectionNameMatch} from './useSectionNameMatch';
import {useSectionPanels} from './useSectionPanels';
import {getDndSortableSectionId} from './utils';
import {WorkspaceFlowLayout} from './WorkspaceFlowLayout';
import {WorkspaceSectionContextProvider} from './WorkspaceSectionContextProvider';
import {WorkspaceSectionLayoutConfigs} from './WorkspaceSectionLayoutConfigs';

type SortablePanelBankSectionProps = {
  idx: number;
  isProjectReadOnly: boolean;
  numVisibleSections: number;
  sectionWithRef: SectionWithRef;
  shouldDisplaySectionContent: boolean;
};

/**
 * This component assumes that a section should be rendered in the workspace.
 */
export const SortablePanelBankSectionContainer = ({
  idx,
  isProjectReadOnly,
  numVisibleSections,
  sectionWithRef,
  shouldDisplaySectionContent,
}: SortablePanelBankSectionProps) => {
  const sectionConfig = useSelector(
    state =>
      state.views.parts['panel-bank-section-config']?.[sectionWithRef.ref.id]
  );

  // When a section is deleted, the sectionConfig will be undefined.
  // This is a workaround to avoid rendering a section that doesn't exist.
  if (!sectionConfig) {
    return null;
  }

  return (
    <WorkspaceSectionContextProvider sectionName={sectionWithRef.name}>
      <SortablePanelBankSection
        idx={idx}
        isProjectReadOnly={isProjectReadOnly}
        numVisibleSections={numVisibleSections}
        sectionWithRef={sectionWithRef}
        shouldDisplaySectionContent={shouldDisplaySectionContent}
      />
    </WorkspaceSectionContextProvider>
  );
};

const SortablePanelBankSection = ({
  idx,
  isProjectReadOnly,
  numVisibleSections,
  sectionWithRef,
  shouldDisplaySectionContent,
}: SortablePanelBankSectionProps) => {
  const sectionComponentRef = useRef<HTMLDivElement | null>(null);

  const sectionRef = sectionWithRef.ref;

  const {debouncedSearchRegex: searchRegex} = usePanelSearchDebouncedContext();
  const isSectionNameSearchMatch = useSectionNameMatch(
    sectionWithRef.name,
    searchRegex
  );

  const {activePanels, filteredPanels} = useSectionPanels(
    sectionWithRef,
    searchRegex,
    isSectionNameSearchMatch
  );

  const isHiddenPanelsSection = useMemo(() => {
    return getIsHiddenPanelsSection({
      idx,
      numSections: numVisibleSections,
      sectionName: sectionWithRef.name,
    });
  }, [idx, numVisibleSections, sectionWithRef.name]);

  const {sectionIsOpen: sectionIsExpanded, shouldRenderSection} =
    usePanelBankSectionIsOpen({
      panelBankSectionConfigRef: sectionRef,
    });

  const {setNodeRef, isOver, active} = useDroppable({
    id: getDndSortableSectionId(sectionWithRef.ref),
    data: {
      // This helps indicate if we're dragging a section or panel
      type: 'section',
      sectionRef: sectionWithRef.ref,
      sectionName: sectionWithRef.name,
      filteredPanelsCount: filteredPanels.length,
      isSectionExpanded: sectionIsExpanded,
    },
  });

  // Hide section content when any section is being dragged for a cleaner reordering experience
  // Using shouldDisplaySectionContent prop is more stable because it's only set once when drag starts
  const shouldDisplayContent = useMemo(() => {
    return shouldDisplaySectionContent && sectionIsExpanded;
  }, [shouldDisplaySectionContent, sectionIsExpanded]);

  const combinedRef = useCallback(
    (node: HTMLDivElement) => {
      setNodeRef(node);
      sectionComponentRef.current = node;
    },
    [setNodeRef]
  );

  const shouldHighlightSection = useMemo(() => {
    if (!isOver) {
      return false;
    }

    // If a panel is being dragged over an empty section
    if (filteredPanels.length === 0) {
      return true;
    }

    // If a panel is being dragged over a collapsed section, highlight the section
    if (!shouldDisplayContent && active?.data.current?.type === 'panel') {
      return true;
    }

    return false;
  }, [active, filteredPanels.length, shouldDisplayContent, isOver]);

  const {showPinnedBorderRef} = useShowPinnedBorder(
    sectionRef,
    sectionComponentRef
  );

  const {targetSectionRef: workspaceDrawerTarget, targetPanelRef} =
    useWorkspaceDrawerContext();
  const isWorkspaceDrawerTarget = useMemo(
    () => isEqual(sectionRef, workspaceDrawerTarget) && targetPanelRef == null,
    [sectionRef, workspaceDrawerTarget, targetPanelRef]
  );

  return (
    <SectionSettingsContextProvider sectionRef={sectionRef}>
      <div
        ref={combinedRef}
        className={twMerge(
          'border-b-[1px] border-moon-300',
          shouldDisplayContent ? 'pb-16' : 'pb-0',
          // If a panel is being dragged over an empty section, highlight the section
          shouldHighlightSection &&
            'dark:bg-teal-250/[0.24] bg-teal-350/[0.24]',
          // Ensure this check for showPinnedBorderRef is not wrapped in useMemo because we are using a ref to
          // prevent unnecessary re-renders.
          (showPinnedBorderRef.current?.sectionRefId === sectionRef.id ||
            isWorkspaceDrawerTarget) &&
            'dark:border-teal-250 overflow-visible border-2 border-teal-350'
        )}
        data-test={`panel-bank-section-[${sectionWithRef.name}]`}>
        <WorkspaceSectionLayoutConfigs
          numTotalPanelsInSection={filteredPanels.length}
          sectionRef={sectionRef}>
          <PanelBankSectionHeader
            activePanelCount={activePanels.length}
            isHiddenPanelsSection={isHiddenPanelsSection}
            isProjectReadOnly={isProjectReadOnly}
            isSectionNameSearchMatch={isSectionNameSearchMatch}
            panelBankSectionConfigRef={sectionRef}
            panelSearchMatchCount={filteredPanels.length}
            sectionComponentRef={sectionComponentRef}
            sectionName={sectionWithRef.name}
          />
          {/*
            Two reasons for using display: none here:
            1. we don't want to render section if it's never opened
            2. we don't want to re-mount an open section if we close and re-open it
            Doing this allows us avoid triggering panel loaders which gives us a
            smoother and more performant experience for users.
          */}
          <div
            className={twMerge(shouldDisplayContent ? 'contents' : 'hidden')}>
            {/*
              We don't want to render the section if it's never opened, but we also don't want
              to re-mount an open section if you collapse and re-open it.
              Take a look at what `shouldRenderSection` does inside of `usePanelBankSectionIsOpen`
           */}
            {shouldDisplayContent && shouldRenderSection && (
              <WorkspaceFlowLayout
                filteredPanels={filteredPanels}
                isProjectReadOnly={isProjectReadOnly}
                panelBankSectionConfigRef={sectionRef}
              />
            )}
          </div>
        </WorkspaceSectionLayoutConfigs>
      </div>
    </SectionSettingsContextProvider>
  );
};
